import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Anchor, PipetteIcon as Pipeline, Ship, Zap, CheckCircle, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import GlobalReachMap from '@/components/GlobalReachMap';

const Home = () => {
  const { t } = useTranslation();

  const stats = [
    { value: '15+', label: t('home.stats.experience') },
    { value: '240', label: t('home.stats.power') },
    { value: '10+', label: t('home.stats.projects') },
    { value: '8', label: t('home.stats.countries') }
  ];

  const services = [
    {
      icon: Ship,
      title: t('home.services.marine.title'),
      desc: t('home.services.marine.desc'),
      link: '/services/marine-construction',
      image: 'https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/axckgrya_liman%20hizmetleri.png'
    },
    {
      icon: Pipeline,
      title: t('home.services.pipeline.title'),
      desc: t('home.services.pipeline.desc'),
      badge: t('home.services.pipeline.badge'),
      link: '/services/pipeline-piping',
      featured: true,
      image: 'https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/wh3xnodo_magic_edit%23TUFITFhWRVp0V2sjMSNhODZkNGE0YWNhNzM1ZmEzMjc5YjlkZmFhZDA3MjE3NiMxMjgwIyNUUkFOU0ZPUk1BVElPTl9SRVFVRVNU.jpg'
    },
    {
      icon: Anchor,
      title: t('home.services.offshore.title'),
      desc: t('home.services.offshore.desc'),
      link: '/services/offshore-operations',
      image: 'https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/8wjmm32s_Offshor%20works%20jpg.jpeg'
    },
    {
      icon: Zap,
      title: t('home.services.energy.title'),
      desc: t('home.services.energy.desc'),
      link: '/services/energy-infrastructure',
      image: 'https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/jgyl5xvl_LNG%20to%20Power.jpeg'
    }
  ];

  const whyChoose = [
    {
      title: t('home.why.local.title'),
      desc: t('home.why.local.desc')
    },
    {
      title: t('home.why.cost.title'),
      desc: t('home.why.cost.desc')
    },
    {
      title: t('home.why.tailored.title'),
      desc: t('home.why.tailored.desc')
    },
    {
      title: t('home.why.track.title'),
      desc: t('home.why.track.desc')
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0">
          <video 
            autoPlay
            muted
            loop
            playsInline
            poster="https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/z8v3a1mj_Fishing%20Harbour%20Rehabilitaion.png"
            className="w-full h-full object-cover"
          >
            <source 
              src="https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/kmo6skdi_Video%20Project%20%281%29.mp4" 
              type="video/mp4" 
            />
          </video>
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-blue-900/80 to-slate-900/90"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
              {t('home.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
              {t('home.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                className="inline-flex items-center px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-2xl"
                data-testid="cta-services"
              >
                {t('home.hero.cta1')}
                <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link
                to="/projects"
                className="inline-flex items-center px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white hover:bg-white/20 text-white font-semibold rounded-lg transition-all"
                data-testid="cta-projects"
              >
                {t('home.hero.cta2')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-amber-400 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-300 text-sm md:text-base">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('home.services.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className={`relative rounded-xl overflow-hidden transition-all hover:shadow-2xl group ${
                    service.featured 
                      ? 'ring-4 ring-amber-400'
                      : 'shadow-lg'
                  }`}
                >
                  {/* Image Header */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className={`absolute inset-0 ${
                      service.featured 
                        ? 'bg-gradient-to-t from-blue-900/95 via-blue-900/50 to-transparent'
                        : 'bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent'
                    }`}></div>
                    {service.badge && (
                      <span className="absolute top-4 right-4 px-3 py-1 bg-amber-400 text-slate-900 text-xs font-bold rounded-full shadow-lg">
                        {service.badge}
                      </span>
                    )}
                    <Icon className="absolute top-4 left-4 text-white drop-shadow-lg" size={36} />
                  </div>

                  {/* Content */}
                  <div className={`p-8 ${
                    service.featured 
                      ? 'bg-gradient-to-br from-blue-900 to-slate-900 text-white'
                      : 'bg-white'
                  }`}>
                    <h3 className={`text-xl font-bold mb-3 ${service.featured ? 'text-white' : 'text-slate-900'}`}>
                      {service.title}
                    </h3>
                    <p className={`mb-4 ${service.featured ? 'text-blue-100' : 'text-gray-600'}`}>
                      {service.desc}
                    </p>
                    <Link
                      to={service.link}
                      className={`inline-flex items-center font-semibold ${
                        service.featured 
                          ? 'text-amber-400 hover:text-amber-300'
                          : 'text-blue-900 hover:text-blue-700'
                      }`}
                    >
                      {t('services.learn')} <ArrowRight className="ml-2" size={16} />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Selimarine */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('home.why.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.why.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whyChoose.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start space-x-4 p-6 bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow"
              >
                <CheckCircle className="text-amber-500 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Reach Map */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t('home.featured.globalReach.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.featured.globalReach.subtitle')}
            </p>
          </div>
          <GlobalReachMap />
        </div>
      </section>

      {/* Featured Project */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('home.featured.title')}
            </h2>
          </div>

          <div className="bg-slate-800 rounded-2xl overflow-hidden shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 lg:p-12">
              <div className="space-y-6">
                <div className="inline-block px-4 py-2 bg-amber-500 text-slate-900 font-bold rounded-full text-sm">
                  {t('projects.hero.badge')}
                </div>
                <h3 className="text-3xl font-bold">
                  {t('home.featured.project.name')}
                </h3>
                <div className="space-y-3 text-blue-200">
                  <div className="flex items-center space-x-3">
                    <MapPin size={20} className="text-amber-400" />
                    <span>{t('home.featured.project.location')}</span>
                  </div>
                  <div><strong>{t('projects.hero.scope')}:</strong> {t('home.featured.project.type')}</div>
                  <div><strong>{t('projects.hero.period')}:</strong> {t('home.featured.project.period')}</div>
                  <div><strong>{t('projects.hero.capacity')}:</strong> {t('home.featured.project.capacity')}</div>
                </div>
                <p className="text-gray-300">
                  {t('home.featured.project.desc')}
                </p>
                <Link
                  to="/projects"
                  className="inline-flex items-center px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-all"
                >
                  {t('home.featured.project.cta')} <ArrowRight className="ml-2" size={20} />
                </Link>
              </div>

              <div className="relative h-64 lg:h-auto rounded-lg overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_web-refresh-plan/artifacts/w0h0dbf5_No%20Name%20FSRU.jpeg"
                  alt="Senegal LNG-to-Power Project - FSRU Vessel"
                  className="w-full h-full object-cover min-h-[400px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t('home.cta.title')}
          </h2>
          <p className="text-xl text-blue-200 mb-8">
            {t('home.cta.subtitle')}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-all transform hover:scale-105"
            data-testid="cta-contact"
          >
            {t('home.cta.button')} <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
